import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  StyledBadge,
  StyledScoreBadge,
  StyledActionBadge,
} from './Badge/Badge.styled';
import { ConcentricCircleChart } from './Badge/ConcentricCircleChart';
import {
  AccessibilityIcon,
  AuditabilityIcon,
  BiodiversityIcon,
  ClimateIcon,
  CommunityIcon,
  EnergyIcon,
  HumanRightsIcon,
  SourcingIcon,
  StrategyIcon,
  TrainingIcon,
  WasteIcon,
  WaterIcon,
} from '../../assets/icons';
import { Hotel } from '../../hooks/useHotels';

// Badge variants using styled components
const badgeVariants = {
  default: 'default',
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
} as const;

const badgeSizes = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
} as const;

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: keyof typeof badgeVariants;
  size?: keyof typeof badgeSizes;
  children: React.ReactNode;
}

export interface ScoreBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  score: number;
}

export interface ESGScoreDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  scoreE: number | null;
  scoreS: number | null;
  scoreG: number | null;
  scoreAlo: number | null;
  isMapMarker?: boolean;
}

export interface ActionBadgeProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  action:
    | 'add_benefits'
    | 'payment_pending'
    | 'unlock'
    | 'boost'
    | 'publish'
    | 'fill_out';
  children: React.ReactNode;
}

export interface FundamentalScoreSegment {
  label: string;
  icon: React.FC<{ size?: number; color?: string }>;
  color: string;
  isCredited: boolean;
}

// Basic Badge Component
export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  className,
  children,
  ...props
}) => {
  return (
    <StyledBadge
      $variant={variant}
      $size={size}
      className={className}
      {...props}
    >
      {children}
    </StyledBadge>
  );
};

// Score Badge Component (Circular with color based on score)
export const ScoreBadge: React.FC<ScoreBadgeProps> = ({
  score,
  className,
  ...props
}) => {
  return (
    <StyledScoreBadge $score={score} className={className} {...props}>
      {score}
    </StyledScoreBadge>
  );
};

// ESG Score Display with Concentric Circles
export const ESGScoreDisplay: React.FC<ESGScoreDisplayProps> = ({
  scoreE,
  scoreS,
  scoreG,
  scoreAlo,
  isMapMarker,
  className,
  ...props
}) => {
  return (
    <div className={`flex flex-col items-center ${className}`} {...props}>
      <ConcentricCircleChart
        scoreE={scoreE}
        scoreG={scoreG}
        scoreS={scoreS}
        totalScore={scoreAlo}
        isMapMarker={isMapMarker}
      />
    </div>
  );
};

export const FundamentalScoreDisplay: React.FC<{
  fundamentalScores: Hotel['fundamental_score'];
  canHoverItems?: boolean;
  isSmallBadge?: boolean;
}> = ({ fundamentalScores, canHoverItems = false, isSmallBadge = false }) => {
  const fs = fundamentalScores;
  const segments = [
    {
      label: 'Biodiversity',
      color: 'var(--color-planet-green-radial)',
      icon: BiodiversityIcon,
      isCredited: fs?.Biodiversity === 1,
    },
    {
      label: 'Water Stewardship',
      color: 'var(--color-planet-green-radial)',
      icon: WaterIcon,
      isCredited: fs?.['Water Stewardship'] === 1,
    },
    {
      label: 'Auditability',
      color: 'var(--color-policy-orange-radial)',
      icon: AuditabilityIcon,
      isCredited: fs?.Auditability === 1,
    },
    {
      label: 'Strategy & Management',
      color: 'var(--color-policy-orange-radial)',
      icon: StrategyIcon,
      isCredited: fs?.['Strategy + Management'] === 1,
    },
    {
      label: 'Training & Engagement',
      color: 'var(--color-policy-orange-radial)',
      icon: TrainingIcon,
      isCredited: fs?.['Training + Engagement'] === 1,
    },
    {
      label: 'Accessibility',
      color: 'var(--color-people-blue-radial)',
      icon: AccessibilityIcon,
      isCredited: fs?.Accessibility === 1,
    },
    {
      label: 'Community & Cultural Stewardship',
      color: 'var(--color-people-blue-radial)',
      icon: CommunityIcon,
      isCredited: fs?.['Community + Cultural Stewardship'] === 1,
    },
    {
      label: 'Human Rights, Justice & Wellbeing',
      color: 'var(--color-people-blue-radial)',
      icon: HumanRightsIcon,
      isCredited: fs?.['Human Rights, Justice + Wellbeing'] === 1,
    },
    {
      label: 'Responsible Sourcing',
      color: 'var(--color-people-blue-radial)',
      icon: SourcingIcon,
      isCredited: fs?.['Responsible Sourcing'] === 1,
    },
    {
      label: 'Climate Action',
      color: 'var(--color-planet-green-radial)',
      icon: ClimateIcon,
      isCredited: fs?.Climate === 1,
    },
    {
      label: 'Energy',
      color: 'var(--color-planet-green-radial)',
      icon: EnergyIcon,
      isCredited: fs?.Energy === 1,
    },
    {
      label: 'Waste',
      color: 'var(--color-planet-green-radial)',
      icon: WasteIcon,
      isCredited: fs?.Waste === 1,
    },
  ];
  const SIZE = 400;
  const CORNER_RADIUS = 4; // controls how rounded outer corners look
  const CENTER = SIZE / 2;
  const OUTER_RADIUS = 180;
  const INNER_RADIUS = 110;
  const ICON_RADIUS = (OUTER_RADIUS + INNER_RADIUS) / 2;
  const creditedSegments = segments.filter(s => s.isCredited).length;

  function polarToCartesian(
    cx: number,
    cy: number,
    radius: number,
    angleDeg: number
  ) {
    const angleRad = (angleDeg - 90) * (Math.PI / 180);
    return {
      x: cx + radius * Math.cos(angleRad),
      y: cy + radius * Math.sin(angleRad),
    };
  }

  function describeArc(startAngle: number, endAngle: number, outerR: number = OUTER_RADIUS) {
    const cornerAngleOffset = (CORNER_RADIUS / outerR) * (180 / Math.PI);

    const adjustedStart = startAngle + cornerAngleOffset;
    const adjustedEnd = endAngle - cornerAngleOffset;

    const outerStart = polarToCartesian(
      CENTER,
      CENTER,
      outerR,
      adjustedStart
    );
    const outerEnd = polarToCartesian(
      CENTER,
      CENTER,
      outerR,
      adjustedEnd
    );

    const innerStart = polarToCartesian(CENTER, CENTER, INNER_RADIUS, endAngle);
    const innerEnd = polarToCartesian(CENTER, CENTER, INNER_RADIUS, startAngle);

    const largeArcFlag = adjustedEnd - adjustedStart <= 180 ? 0 : 1;

    return `
    M ${outerStart.x} ${outerStart.y}
    A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}
    A ${CORNER_RADIUS} ${CORNER_RADIUS} 0 0 1 ${
      polarToCartesian(CENTER, CENTER, outerR - CORNER_RADIUS, endAngle).x
    } ${
      polarToCartesian(CENTER, CENTER, outerR - CORNER_RADIUS, endAngle).y
    }
    L ${innerStart.x} ${innerStart.y}
    A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArcFlag} 0 ${innerEnd.x} ${innerEnd.y}
    L ${
      polarToCartesian(CENTER, CENTER, outerR - CORNER_RADIUS, startAngle)
        .x
    } ${
      polarToCartesian(CENTER, CENTER, outerR - CORNER_RADIUS, startAngle)
        .y
    }
    A ${CORNER_RADIUS} ${CORNER_RADIUS} 0 0 1 ${outerStart.x} ${outerStart.y}
    Z
  `;
  }
  const svgRef = useRef<SVGSVGElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [tooltipScreen, setTooltipScreen] = useState<{ x: number; y: number; midAngle: number } | null>(null);

  return (
    <>
    <svg
      ref={svgRef}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Segments */}
      {segments.map((segment, i) => {
        const angleStep = 360 / segments.length;
        const GAP = 3;

        const startAngle = i * angleStep + GAP / 2;
        const endAngle = (i + 1) * angleStep - GAP / 2;
        const midAngle = startAngle + (endAngle - startAngle) / 2;

        const iconPos = polarToCartesian(CENTER, CENTER, ICON_RADIUS, midAngle);

        const Icon = segment.icon;
        const ICON_SIZE = 28;
        const HALF_ICON = ICON_SIZE / 2;

        const isActive = canHoverItems && activeIndex === i;
        const HOVER_EXTRA = 12;
        const activeOuterR = isActive ? OUTER_RADIUS + HOVER_EXTRA : OUTER_RADIUS;
        const activeIconRadius = (activeOuterR + INNER_RADIUS) / 2;
        const activeIconPos = isActive
          ? polarToCartesian(CENTER, CENTER, activeIconRadius, midAngle)
          : iconPos;

        return (
          <g
            key={i}
            onMouseEnter={() => {
              setActiveIndex(i);
              if (canHoverItems && svgRef.current) {
                const hoverLabelRadius = OUTER_RADIUS + HOVER_EXTRA + 10;
                const hoverLabelPos = polarToCartesian(CENTER, CENTER, hoverLabelRadius, midAngle);
                const pt = svgRef.current.createSVGPoint();
                pt.x = hoverLabelPos.x;
                pt.y = hoverLabelPos.y;
                const ctm = svgRef.current.getScreenCTM();
                if (ctm) {
                  const screenPt = pt.matrixTransform(ctm);
                  setTooltipScreen({ x: screenPt.x, y: screenPt.y, midAngle });
                }
              }
            }}
            onMouseLeave={() => {
              setActiveIndex(null);
              setTooltipScreen(null);
            }}
            style={{ cursor: canHoverItems ? 'pointer' : 'default' }}
          >
            {/* Segment */}
            <path
              d={describeArc(startAngle, endAngle, activeOuterR)}
              fill={segment.isCredited ? segment.color : '#d7dfe9'}
              style={{ transition: 'd 0.25s ease-out' }}
            />

            {/* Icon */}
            <g
              style={{ transition: 'transform 0.25s ease-out' }}
              transform={`
                 translate(${activeIconPos.x}, ${activeIconPos.y})
                 translate(${-HALF_ICON}, ${-HALF_ICON})
   `}
            >
              <Icon
                size={ICON_SIZE}
                color={segment.isCredited ? 'white' : 'gray'}
              />
            </g>
          </g>
        );
      })}
      {/* Center Circle */}
      <circle cx={CENTER} cy={CENTER} r={INNER_RADIUS - 5} fill="#d7dfe9" />

      {/* Center Text */}
      {fundamentalScores === null ? (
        <text
          x={CENTER}
          y={isSmallBadge ? CENTER + 14 : CENTER - 25}
          textAnchor="middle"
          fontSize="40"
          fill="var(--color-gray-800)"
          fontWeight="600"
        >
          NA
        </text>
      ) : (
        <>
          <text
            x={CENTER}
            y={isSmallBadge ? CENTER + 14 : CENTER - 25}
            textAnchor="middle"
            fontSize="40"
            fill="var(--color-gray-800)"
            fontWeight="600"
          >
            {creditedSegments}
          </text>
        </>
      )}
      {!isSmallBadge && (
        <>
          <text
            x={CENTER}
            y={CENTER + 25}
            textAnchor="middle"
            fontSize="28"
            fontWeight="500"
          >
            Alō Index
          </text>

          <text
            x={CENTER}
            y={CENTER + 50}
            textAnchor="middle"
            fontSize="18"
            fontWeight="600"
            fill='blue'
          >
            {`Fundamentals`.toUpperCase()}
          </text>
        </>
      )}
    </svg>
    {activeIndex !== null && tooltipScreen && canHoverItems && createPortal(
      (() => {
        const segment = segments[activeIndex];
        const { x, y, midAngle: hoverMidAngle } = tooltipScreen;
        const isBottomSegment = hoverMidAngle >= 160 && hoverMidAngle <= 200;
        const isRightSide = hoverMidAngle < 160;
        const maxWidth = 160;
        const GAP = 10;

        let left: number, top: number, textAlign: 'center' | 'left' | 'right';
        if (isBottomSegment) {
          left = x - maxWidth / 2;
          top = y;
          textAlign = 'center';
        } else {
          left = isRightSide ? x + GAP : x - maxWidth - GAP;
          top = y - 20;
          textAlign = isRightSide ? 'left' : 'right';
        }

        return (
          <>
          <style>{`
            @keyframes tooltipFadeIn {
              from { opacity: 0; transform: translateY(4px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <div
            style={{
              position: 'fixed',
              left,
              top,
              width: maxWidth,
              pointerEvents: 'none',
              zIndex: 9999,
              fontSize: '14px',
              color: segment.color,
              fontWeight: '600',
              textAlign,
              lineHeight: '1.2',
              wordWrap: 'break-word',
              animation: 'tooltipFadeIn 0.2s ease-out',
            }}
          >
            {segment.label}
            {!segment.isCredited && (
              <div style={{ fontSize: '12px', color: '#888', fontWeight: '400' }}>
                ( Incompleted )
              </div>
            )}
          </div>
          </>
        );
      })()
      , document.body
    )}
    </>
  );
};

// Action Badge Component (Button-like badge for actions)
export const ActionBadge: React.FC<ActionBadgeProps> = ({
  action,
  className,
  children,
  ...props
}) => {
  return (
    <StyledActionBadge $action={action} className={className} {...props}>
      {children}
    </StyledActionBadge>
  );
};
